import React, { useState, useEffect, useContext, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaHome, FaPlus, FaClipboardList, FaBell } from 'react-icons/fa';
import { IoChatbubbles } from 'react-icons/io5';
import { useChat } from './Chat/ChatContext';
import { useNotifications } from './Notifications/NotificationContext';
import { AuthContext } from './Provider';
import CreateMenu from './CreateMenu';
import MyActivePostsDropdown from './MyActivePosts/MyActivePostsDropdown';
import NotificationDropdown from './Notifications/NotificationDropdown';
import './BottomNavBar.css';

// Shared-layout spring for the sliding active indicator (Material 3 Expressive /
// iOS 26 style travel between destinations). Calm, no overshoot bounce.
const NAV_SPRING = { type: 'spring', stiffness: 480, damping: 38, mass: 0.7 };

// One indicator element, reused (same layoutId) so Framer animates it gliding
// from the previously-active tab to the newly-active one.
const ActivePill = () => (
  <motion.span
    layoutId="navActivePill"
    className="nav-active-indicator"
    transition={NAV_SPRING}
    aria-hidden="true"
  />
);

function BottomNavBar() {
  const { user, loading } = useContext(AuthContext);
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const location = useLocation();
  const { toggleChat, closeChat, isChatOpen, otherUnreadMessages } = useChat();
  const { unreadCount, isDropdownOpen, toggleDropdown, closeDropdown } = useNotifications();
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [isCreateMenuOpen, setIsCreateMenuOpen] = useState(false);
  const [isMyPostsDropdownOpen, setIsMyPostsDropdownOpen] = useState(false);

  // Any open panel (dropdown/menu/chat). While one is open we keep the bar
  // visible — its pop-ups are anchored to the bar, so hiding it drags them off.
  const anyPanelOpen = isCreateMenuOpen || isMyPostsDropdownOpen || isDropdownOpen || isChatOpen;
  const anyPanelOpenRef = useRef(anyPanelOpen);
  useEffect(() => { anyPanelOpenRef.current = anyPanelOpen; }, [anyPanelOpen]);

  // Route-aware "you are here": Home is active only when we're on it AND no
  // overlay panel is taking focus.
  const isHomeActive = (location.pathname === '/home' || location.pathname === '/') && !anyPanelOpen;

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const currentScrollY = window.scrollY;
          const delta = currentScrollY - lastScrollY;

          // Always show at the very top of the page.
          if (currentScrollY < 10) {
            setIsVisible(true);
            setLastScrollY(currentScrollY);
          } else if (Math.abs(delta) > 8) {
            // Deadzone: ignore sub-8px jitter so momentum / overscroll-bounce
            // doesn't flicker the show/hide transition. Only commit (and move the
            // reference point) once movement clears the threshold.
            if (delta < 0) {
              setIsVisible(true); // scrolling up
            } else if (currentScrollY > 100 && !anyPanelOpenRef.current) {
              setIsVisible(false); // scrolling down, not near top, no panel open
            }
            setLastScrollY(currentScrollY);
          }

          ticking = false;
        });
        
        ticking = true;
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [lastScrollY]);

  const handleNavigation = (path) => {
    navigate(path);
  };

  const handleHomeClick = () => {
    // Already on Home → scroll back to the top instead of a no-op navigate.
    if (location.pathname === '/home' || location.pathname === '/') {
      // Close any open panel first so the jump-to-top isn't obscured.
      closeChat();
      closeDropdown();
      setIsCreateMenuOpen(false);
      setIsMyPostsDropdownOpen(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/home');
    }
  };

  const handleCreateClick = () => {
    // Close other dropdowns before toggling
    if (!isCreateMenuOpen) {
      closeChat();
      closeDropdown();
      setIsMyPostsDropdownOpen(false);
    }
    setIsCreateMenuOpen(!isCreateMenuOpen);
  };

  const closeCreateMenu = () => {
    setIsCreateMenuOpen(false);
  };

  const handleMyPostsClick = () => {
    // Close other dropdowns before toggling
    if (!isMyPostsDropdownOpen) {
      closeChat();
      closeDropdown();
      setIsCreateMenuOpen(false);
    }
    setIsMyPostsDropdownOpen(!isMyPostsDropdownOpen);
  };

  const closeMyPostsDropdown = () => {
    setIsMyPostsDropdownOpen(false);
  };

  const handleNotificationClick = () => {
    // Close other dropdowns before toggling
    if (!isDropdownOpen) {
      closeChat();
      setIsCreateMenuOpen(false);
      setIsMyPostsDropdownOpen(false);
    }
    toggleDropdown();
  };

  const handleChatClick = () => {
    // Close other dropdowns before toggling
    if (!isChatOpen) {
      closeDropdown();
      setIsCreateMenuOpen(false);
      setIsMyPostsDropdownOpen(false);
    }
    toggleChat();
  };

  // Close notification dropdown when scrolling the page
  useEffect(() => {
    if (!isDropdownOpen) return;

    const handlePageScroll = () => {
      // Close dropdown when user scrolls the page
      toggleDropdown();
    };

    // Add scroll listener to window
    window.addEventListener('scroll', handlePageScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handlePageScroll);
    };
  }, [isDropdownOpen, toggleDropdown]);

  // Don't render bottom nav bar if user is not authenticated
  if (!user || loading) {
    return null;
  }

  return (
    <nav
      className={`bottom-nav-bar ${isVisible ? 'visible' : 'hidden'}`}
      aria-label={t('bottom_nav.navigation', 'Primary navigation')}
    >
      <div className="bottom-nav-container">
        <button
          className={`nav-item ${isHomeActive ? 'active' : ''}`}
          onClick={handleHomeClick}
          title={t('bottom_nav.home')}
          aria-label={t('bottom_nav.home')}
          aria-current={isHomeActive ? 'page' : undefined}
        >
          {isHomeActive && <ActivePill />}
          <FaHome className="nav-icon" />
          <span className="nav-label">{t('bottom_nav.home')}</span>
        </button>

        <button
          className={`nav-item notification-nav-button NTS-notification-bell-container ${isDropdownOpen ? 'active' : ''}`}
          onClick={handleNotificationClick}
          title={t('bottom_nav.notifications')}
          aria-label={t('bottom_nav.notifications')}
          aria-haspopup="true"
          aria-expanded={isDropdownOpen}
        >
          {isDropdownOpen && <ActivePill />}
          <div className="notification-icon-wrapper">
            <FaBell className="nav-icon" />
            {unreadCount > 0 && (
              <div className="nav-unread-badge">{unreadCount > 99 ? '99+' : unreadCount}</div>
            )}
          </div>
          <span className="nav-label">{t('bottom_nav.notifications')}</span>
        </button>

        <button
          className={`nav-item center-button ${isCreateMenuOpen ? 'active' : ''}`}
          onClick={handleCreateClick}
          title={t('bottom_nav.create')}
          aria-label={t('bottom_nav.create')}
          aria-haspopup="true"
          aria-expanded={isCreateMenuOpen}
        >
          <div className="center-icon-wrapper">
            <FaPlus className="nav-icon" />
          </div>
          <span className="nav-label">{t('bottom_nav.create')}</span>
        </button>

        <button
          className={`nav-item ${isMyPostsDropdownOpen ? 'active' : ''}`}
          onClick={handleMyPostsClick}
          title={t('bottom_nav.my_posts')}
          aria-label={t('bottom_nav.my_posts')}
          aria-haspopup="true"
          aria-expanded={isMyPostsDropdownOpen}
        >
          {isMyPostsDropdownOpen && <ActivePill />}
          <FaClipboardList className="nav-icon" />
          <span className="nav-label">{t('bottom_nav.my_posts')}</span>
        </button>

        <button
          className={`nav-item chat-nav-button ${isChatOpen ? 'active' : ''}`}
          onClick={handleChatClick}
          title={t('bottom_nav.chat')}
          aria-label={t('bottom_nav.chat')}
          aria-haspopup="true"
          aria-expanded={isChatOpen}
        >
          {isChatOpen && <ActivePill />}
          <div className="chat-icon-wrapper">
            <IoChatbubbles className="nav-icon" />
            {otherUnreadMessages.size > 0 && (
              <div className="nav-unread-badge">{otherUnreadMessages.size}</div>
            )}
          </div>
          <span className="nav-label">{t('bottom_nav.chat')}</span>
        </button>
      </div>
      
      {/* Create Menu Dropdown */}
      <CreateMenu isOpen={isCreateMenuOpen} onClose={closeCreateMenu} />
      
      {/* My Active Posts Dropdown */}
      <MyActivePostsDropdown 
        isOpen={isMyPostsDropdownOpen} 
        onClose={closeMyPostsDropdown} 
      />
      
      {/* Notification Dropdown */}
      {isDropdownOpen && (
        <div className="bottom-nav-notification-dropdown-container NTS-notification-bell-container">
          <NotificationDropdown />
        </div>
      )}
    </nav>
  );
}

export default BottomNavBar;
